<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import axios from 'axios'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ArrowRight, Loader2 } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { type TurtleProcessLink } from '@/utils/processTurtle'
import {
  initialProcessDiagramTab,
  processDiagramTabs,
  type ProcessDiagramTab,
} from '@/utils/processPresentation'
import TurtleChartEditor from './TurtleChartEditor.vue'
import ProcessFlowDesigner from './ProcessFlowDesigner.vue'

const props = defineProps<{ open: boolean; links: TurtleProcessLink[]; processes?: any[] }>()
const emit = defineEmits<{
  (event: 'update:open', value: boolean): void
  (event: 'open-process', id: number): void
}>()
const { t } = useI18n()
const displayedLinks = ref<TurtleProcessLink[]>([])
const selected = ref<TurtleProcessLink | null>(null)
const preview = ref<any>(null)
const loading = ref(false)
const error = ref('')
const activeDiagram = ref<ProcessDiagramTab | null>(null)

watch(() => [props.open, props.links], () => {
  displayedLinks.value = [...props.links]
  selected.value = displayedLinks.value.length === 1 ? displayedLinks.value[0] : null
  preview.value = null
  activeDiagram.value = null
  error.value = ''
  if (!props.open) return
  if (selected.value) loadPreview(selected.value)
}, { deep: true })

async function loadPreview(link: TurtleProcessLink) {
  selected.value = link
  preview.value = null
  loading.value = true
  error.value = ''
  try {
    if (link.release_id) {
      const response = await axios.get(`/process_management/releases/${link.release_id}`)
      preview.value = { ...response.data.snapshot, released: true, release_number: link.release_number, pinned: true }
    } else {
      const response = await axios.get(`/process_management/processes/${link.process_id}`)
      preview.value = response.data.process
    }
    activeDiagram.value = initialProcessDiagramTab(preview.value)
  } catch (requestError: any) {
    error.value = requestError.response?.data?.error || t('processManagement.linkedProcessLoadFailed')
  } finally { loading.value = false }
}

const ownerName = computed(() => [preview.value?.owner?.firstname, preview.value?.owner?.lastname].filter(Boolean).join(' ') || preview.value?.owner?.email || '—')
const diagramTabs = computed(() => processDiagramTabs(preview.value))
const previewProcesses = computed(() => (props.processes || []).filter((process) => process.id !== selected.value?.process_id))

function openLinkedPreviews(links: TurtleProcessLink[]) {
  displayedLinks.value = [...links]
  selected.value = displayedLinks.value.length === 1 ? displayedLinks.value[0] : null
  preview.value = null
  activeDiagram.value = null
  error.value = ''
  if (selected.value) loadPreview(selected.value)
}

function openFullProcess() {
  if (!selected.value) return
  emit('update:open', false)
  emit('open-process', selected.value.process_id)
}
</script>

<template>
  <Sheet :open="open" @update:open="emit('update:open', $event)">
    <SheetContent side="right" class="z-[2600] w-[min(1180px,96vw)] overflow-y-auto sm:max-w-none" overlay-class="z-[2590]" :disable-outside-pointer-events="false">
      <SheetHeader class="mb-5"><SheetTitle>{{ t('processManagement.linkedProcessesTitle') }}</SheetTitle></SheetHeader>

      <div v-if="displayedLinks.length > 1" class="mb-5 grid gap-2 sm:grid-cols-2">
        <button v-for="link in displayedLinks" :key="`${link.process_id}-${link.release_id || 'current'}`" type="button" class="rounded-lg border p-3 text-left hover:border-[#80BA27]" :class="selected?.process_id === link.process_id ? 'border-[#80BA27] bg-emerald-50' : ''" @click="loadPreview(link)">
          <div class="text-xs font-semibold text-slate-500">{{ link.code || `#${link.process_id}` }}</div>
          <div class="font-medium">{{ link.title || t('processManagement.unavailableProcess') }}</div>
          <div v-if="link.release_number" class="mt-1 text-xs text-slate-500">{{ t('processManagement.pinnedVersion') }} v{{ link.release_number }}</div>
        </button>
      </div>

      <div v-if="!selected" class="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">{{ t('processManagement.selectLinkedProcess') }}</div>
      <div v-else-if="loading" class="flex justify-center p-12 text-slate-500"><Loader2 class="mr-2 h-5 w-5 animate-spin" />{{ t('processManagement.loading') }}</div>
      <div v-else-if="error" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{{ error }}</div>
      <div v-else-if="preview" class="space-y-5">
        <header class="rounded-xl border bg-slate-50 p-4">
          <div class="flex flex-wrap items-start justify-between gap-3"><div><div class="text-xs font-semibold uppercase text-slate-500">{{ preview.code }}</div><h2 class="text-xl font-semibold">{{ preview.title }}</h2></div><span class="rounded-full bg-emerald-100 px-3 py-1 text-xs text-emerald-800">{{ preview.pinned ? t('processManagement.pinnedVersion') : (preview.unreleased_changes ? t('processManagement.draft') : t('processManagement.released')) }}<template v-if="preview.release_number"> · v{{ preview.release_number }}</template></span></div>
          <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2"><div><dt class="text-xs text-slate-500">{{ t('processManagement.category') }}</dt><dd>{{ t(`processManagement.categories.${preview.category}`) }}</dd></div><div><dt class="text-xs text-slate-500">{{ t('processManagement.owner') }}</dt><dd>{{ ownerName }}</dd></div></dl>
        </header>
        <nav v-if="diagramTabs.length > 1" class="flex gap-1 border-b" :aria-label="t('processManagement.diagramPreview')">
          <button
            v-for="tab in diagramTabs"
            :key="tab"
            type="button"
            class="border-b-2 px-4 py-2 text-sm font-medium"
            :class="activeDiagram === tab ? 'border-[#80BA27] text-slate-900' : 'border-transparent text-slate-500'"
            @click="activeDiagram = tab"
          >
            {{ t(`processManagement.tabs.${tab}`) }}
          </button>
        </nav>

        <section v-if="activeDiagram === 'turtle'" class="rounded-xl border bg-white p-4">
          <TurtleChartEditor
            :model-value="preview.turtle_data"
            :process="preview"
            :processes="previewProcesses"
            readonly
            @open-links="openLinkedPreviews"
          />
        </section>
        <section v-else-if="activeDiagram === 'flow'" class="rounded-xl border bg-white p-3">
          <ProcessFlowDesigner :model-value="preview.flow_data" readonly />
        </section>
        <div v-else class="rounded-xl border border-dashed p-10 text-center text-sm text-slate-500">
          {{ t('processManagement.noDiagramPreview') }}
        </div>
        <Button @click="openFullProcess">{{ t('processManagement.openFullProcess') }}<ArrowRight class="ml-2 h-4 w-4" /></Button>
      </div>
    </SheetContent>
  </Sheet>
</template>
